import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "./Card";
import InfoModal from "./InfoModal";
import { fallbackCardByType } from "./CardsGeneral";
import SVG from "../../../Components/SVG";
import { API_BASE_URL } from "../../../config/api";

export default function CardsForYou() {
  const navigate = useNavigate();
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoModalData, setInfoModalData] = useState(null);
  const [gainedCertificates, setGainedCertificates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchCertificates = useCallback(async () => {
    try {
      let response = await fetch(`${API_BASE_URL}certificates/certificates`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
      });

      if (response.status === 401) {
        const refreshResponse = await fetch(
          `${API_BASE_URL}auth/trader/jwt/refresh/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              refresh: localStorage.getItem("refresh"),
            }),
          },
        );

        if (!refreshResponse.ok) {
          localStorage.removeItem("access");
          localStorage.removeItem("authCredentials");
          localStorage.removeItem("refresh");
          localStorage.removeItem("role");
          localStorage.removeItem("email");
          navigate("/signin");
          return;
        }

        const refreshData = await refreshResponse.json();
        localStorage.setItem("access", refreshData.access);

        response = await fetch(`${API_BASE_URL}certificates/certificates`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        });
      }

      if (!response.ok) {
        throw new Error(`https error! Status: ${response.status}`);
      }

      const data = await response.json();
      setGainedCertificates(Array.isArray(data?.gained) ? data.gained : []);
    } catch (error) {
      console.error("Error fetching certificate data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchCertificates();
  }, [fetchCertificates]);

  const cards = useMemo(() => {
    return gainedCertificates.map((certificate) => {
      const fallbackCard =
        fallbackCardByType[certificate.certificate_type] || {};

      return {
        ...fallbackCard,
        ...certificate,
        key: `gained-${certificate.id}`,
        title: fallbackCard.title || certificate.certificate_type,
        imageSrc: certificate.file,
        downloadUrl: certificate.file,
        isForExplanation: false,
        isAchieved: false,
        isLock: false,
        isBlured: false,
      };
    });
  }, [gainedCertificates]);

  return (
    <div className="relative w-full">
      {!isLoading && cards.length > 0 && (
        <div className="w-full rounded-2xl p-5 grid grid-cols-1 lg:grid-cols-2 gap-12 justify-items-center bg-zi-bg2-light dark:bg-zi-bg2-dark">
          {cards.map((item) => (
            <Card
              key={item.key}
              title={item.title}
              imageSrc={item.imageSrc}
              downloadUrl={item.downloadUrl}
              isForExplanation={item.isForExplanation}
              isAchieved={item.isAchieved}
              isLock={item.isLock}
              isBlured={item.isBlured}
              onHelp={() => {
                setInfoModalData(item);
                setShowInfoModal(true);
              }}
            />
          ))}
        </div>
      )}

      {(isLoading || cards.length === 0) && (
        <div className="w-full h-full flex flex-col items-center justify-center gap-4 text-zi-text-light dark:text-zi-text-dark">
          <SVG name="noCertificate" classname="size-32 sm:size-48" />
          <span className="text-center font-normal text-sm sm:text-base text-zi-text-light dark:text-zi-text-dark">
            <p className="font-medium text-base sm:text-lg">
              فعلا گواهی‌نامه‌ای ندارید.
            </p>
            <p className="text-zi-text-light/75 dark:text-zi-text-dark/75">
              جهت مشاهده لیست گواهی‌نامه‌ها و نحوه دریافت آن‌ها بخش معرفی
              گواهی‌نامه‌ها را مطالعه کنید.
            </p>
          </span>
        </div>
      )}

      {showInfoModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setShowInfoModal(false)}
        >
          <div
            className="w-11/12 max-w-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <InfoModal
              title={infoModalData?.title}
              infoText={infoModalData?.infoText}
              onClose={() => {
                setShowInfoModal(false);
                setInfoModalData(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
